import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, BookOpenText, CalendarDays, ExternalLink, Eye, Library, Loader2, PlayCircle, Search, Sparkles } from "lucide-react";
import { fadeUp, revealProps } from "@/lib/motion";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { API_BASE, MAX_CHANNEL_LINK } from "@/config/links";
import { trackClick } from "@/lib/track";
import { useSeo } from "@/lib/seo";

const PLACEHOLDER_COVER =
  "https://images.unsplash.com/photo-1595319260223-c068746347cc?crop=entropy&cs=srgb&fm=jpg&q=85";

function shortDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function monthKey(value) {
  return /^\d{4}-\d{2}/.test(value || "") ? value.slice(0, 7) : "unknown";
}

function monthLabel(key) {
  if (key === "unknown") return "Без даты";
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
}

function formatViews(value) {
  return Number(value || 0).toLocaleString("ru-RU");
}

// fetch при недоступном API кидает TypeError "Failed to fetch" — латиницу пользователю не показываем.
function friendlyError(error, fallback) {
  const message = error?.message || "";
  return /[А-Яа-яЁё]/.test(message) ? message : fallback;
}

function groupStoriesByMonth(stories) {
  const groups = new Map();
  for (const story of stories) {
    const key = monthKey(story.date);
    if (!groups.has(key)) groups.set(key, { key, label: monthLabel(key), items: [] });
    groups.get(key).items.push(story);
  }
  return [...groups.values()];
}

const rubricPillClass = (active) =>
  `w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium border ${
    active
      ? "bg-amber/15 text-amber-soft border-amber/25"
      : "text-muted-foreground hover:bg-night-card hover:text-cream border-transparent"
  }`;

const StoriesPage = () => {
  useSeo({
    title: "Сказки детям на ночь — новая сказка каждый вечер",
    description:
      "Новая сказка каждый день в 18:00: текст, озвучка, иногда видео. Ищите по названию или выбирайте тему.",
    path: "/stories",
  });
  const [state, setState] = useState({ loading: true, error: "", stories: [] });
  const [query, setQuery] = useState("");
  const [rubric, setRubric] = useState("");

  useEffect(() => {
    let alive = true;
    async function loadStories() {
      try {
        const response = await fetch(`${API_BASE}/api/stories?limit=200`);
        if (!response.ok) throw new Error("Не удалось загрузить сказки");
        const data = await response.json();
        if (alive) setState({ loading: false, error: "", stories: data.stories || [] });
      } catch (error) {
        if (alive) setState({ loading: false, error: friendlyError(error, "Сказки временно недоступны"), stories: [] });
      }
    }
    loadStories();
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => {
    const total = state.stories.length;
    const withVideo = state.stories.filter((story) => story.hasVideo).length;
    return { total, withVideo, texts: total };
  }, [state.stories]);

  const rubrics = useMemo(() => {
    const counts = new Map();
    for (const story of state.stories) {
      if (!story.rubric) continue;
      counts.set(story.rubric, (counts.get(story.rubric) || 0) + 1);
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b, "ru"));
  }, [state.stories]);

  const filteredStories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.stories.filter((story) => {
      if (rubric && story.rubric !== rubric) return false;
      if (!q) return true;
      return [story.title, story.excerpt, story.theme].some((field) => field && field.toLowerCase().includes(q));
    });
  }, [state.stories, query, rubric]);

  const storyGroups = useMemo(() => groupStoriesByMonth(filteredStories), [filteredStories]);

  return (
    <div className="min-h-screen bg-night-900 text-cream">
      <Header />
      <div className="max-w-7xl mx-auto lg:flex">
        {rubrics.length ? (
          <aside className="hidden lg:block w-64 shrink-0 border-r border-line sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto px-4 py-8">
            <h2 className="font-serif text-xl text-cream px-4 mb-5">По темам</h2>

            <button onClick={() => setRubric("")} className={`${rubricPillClass(!rubric)} mb-1`}>
              <span className="flex items-center gap-3">
                <Library className="h-4 w-4" />
                Все сказки
              </span>
              <span className="text-xs tabular-nums">{state.stories.length}</span>
            </button>

            <div className="my-3 mx-4 h-px bg-line" />

            <ul className="space-y-1">
              {rubrics.map((r) => (
                <li key={r.name}>
                  <button onClick={() => setRubric(r.name)} className={rubricPillClass(rubric === r.name)}>
                    <span className="flex items-center gap-3 min-w-0">
                      <BookOpen className="h-4 w-4 shrink-0" />
                      <span className="truncate">{r.name}</span>
                    </span>
                    <span className="text-xs tabular-nums">{r.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}

        <main className="flex-1 min-w-0 animate-fade-slide px-4 sm:px-6 lg:px-10 py-10">
          <section className="mb-10">
            <p className="text-[10px] uppercase tracking-[0.24em] text-amber font-semibold mb-4">
              Каждый вечер
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] text-cream max-w-3xl">
              {rubric ? (
                <span className="text-amber-soft">{rubric}</span>
              ) : (
                <>
                  Сказки <span className="italic text-muted-foreground">на каждый вечер</span>
                </>
              )}
            </h1>
            <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
              Новая сказка каждый день в 18:00: текст, озвучка, иногда видео. Ищите по названию или выбирайте тему.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <BookOpenText className="h-4 w-4 text-amber" />
                <span className="tabular-nums text-cream font-medium">{stats.total}</span> сказок
              </div>
              <div className="h-4 w-px bg-line-strong" />
              <div className="flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-amber" />
                <span className="tabular-nums text-cream font-medium">{stats.withVideo}</span> с видео
              </div>
            </div>
          </section>

          {state.stories.length ? (
            <section className="mb-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-dim" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Найти сказку…"
                  className="w-full h-12 pl-11 pr-4 rounded-full bg-night-card border border-line-strong text-sm text-cream placeholder:text-dim focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/25 transition-colors"
                />
              </div>
              {rubrics.length ? (
                <select
                  value={rubric}
                  onChange={(e) => setRubric(e.target.value)}
                  className="lg:hidden h-11 rounded-full bg-night-card border border-line-strong text-sm text-cream px-4 focus:outline-none focus:border-amber sm:max-w-xs"
                >
                  <option value="">Все сказки</option>
                  {rubrics.map((r) => (
                    <option key={r.name} value={r.name}>
                      {r.name} · {r.count}
                    </option>
                  ))}
                </select>
              ) : null}
            </section>
          ) : null}

          {state.loading ? (
            <div className="flex min-h-52 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Загружаю сказки
            </div>
          ) : state.error ? (
            <div className="rounded-2xl border border-amber/30 bg-amber/10 px-5 py-5 text-amber-soft">{state.error}</div>
          ) : filteredStories.length ? (
            <div>
              {storyGroups.map((group) => (
                <section key={group.key} className="mb-14">
                  <div className="flex items-center gap-4 mb-6">
                    <h2 className="font-serif text-2xl sm:text-3xl text-cream capitalize whitespace-nowrap">
                      {group.label}
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-r from-line-strong via-line-strong/40 to-transparent" />
                    <span className="text-xs text-dim tabular-nums">{group.items.length}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {group.items.map((story) => (
                      <motion.div key={story.sourceKey} {...revealProps} variants={fadeUp} className="flex">
                      <Link
                        to={story.slug ? `/stories/${story.sourceKey}/${story.slug}` : `/stories/${story.sourceKey}`}
                        className="group w-full bg-night-card rounded-3xl overflow-hidden border border-line hover:border-amber/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(245,183,74,0.3)] flex flex-col"
                      >
                        <div className="relative w-full aspect-[4/3] overflow-hidden bg-night-900">
                          <img
                            src={story.coverUrl || PLACEHOLDER_COVER}
                            alt={story.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-night-900/85 via-transparent to-transparent pointer-events-none" />
                          <div className="absolute top-3 left-3 flex gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full bg-night-950/75 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-cream border border-line">
                              {story.hasVideo ? <PlayCircle className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
                              {story.hasVideo ? "Видео" : "Текст"}
                            </span>
                          </div>
                          {story.hasVideo && (
                            <div className="absolute top-3 right-3">
                              <span className="inline-flex items-center gap-1 rounded-full bg-night-950/75 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-cream border border-line">
                                <Eye className="h-3 w-3" />
                                {formatViews(story.views)}
                              </span>
                            </div>
                          )}
                          {story.rubric && (
                            <div className="absolute bottom-3 left-3 right-3">
                              <span className="inline-flex items-center rounded-full bg-amber backdrop-blur px-2.5 py-0.5 text-[10px] font-semibold text-night-900">
                                {story.rubric}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="p-5 flex flex-col flex-1 gap-3">
                          <div className="flex items-center gap-1.5 text-xs text-dim">
                            <CalendarDays className="h-3 w-3" />
                            <span>{shortDate(story.date)}</span>
                          </div>
                          <h3 className="font-serif text-xl leading-tight text-cream line-clamp-2 group-hover:text-cream transition-colors">
                            {story.title}
                          </h3>
                          {story.excerpt && (
                            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{story.excerpt}</p>
                          )}
                          <div className="mt-auto pt-2 text-xs text-amber font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Читать сказку →
                          </div>
                        </div>
                      </Link>
                      </motion.div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : state.stories.length ? (
            <div className="rounded-2xl border border-line bg-night-card p-6 text-muted-foreground">
              {query ? <>Ничего не нашлось по запросу «{query}».</> : "Ничего не нашлось по выбранной теме."}
            </div>
          ) : (
            <div className="mt-6 border border-dashed border-line-strong rounded-2xl px-8 py-16 text-center">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber/15 border border-amber/25 mb-5">
                <Sparkles className="h-6 w-6 text-amber" />
              </div>
              <h2 className="font-serif text-2xl text-cream">Сказки скоро появятся</h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Новые ежедневные сказки будут появляться здесь после публикации в канале.
              </p>
              <a
                href={MAX_CHANNEL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("channel")}
                className="inline-flex items-center gap-2 mt-6 rounded-full bg-amber hover:bg-amber-hover text-night-900 px-6 h-11 text-sm font-medium transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Открыть MAX-канал
              </a>
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default StoriesPage;
